import { useState, useEffect, useCallback, Suspense } from "react";
import logo from "./assets/Logo.webp";
import { lazyWithReload } from "./utils/lazyWithReload";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { auth } from "./firebase";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { captureReferralCode } from "./utils/referral";
import { startHeatmap } from "./utils/heatmap";
import { productPath } from "./seo/productSeo";
import { GUIDE_LINKS } from "./guides/slugs";
import LanguageSuggest from "./components/LanguageSuggest";
import { LANGS, LANG_INFO } from "./guides/i18n";

/* The sign-in popup, with its artwork and form code, loads on first use
   rather than with every page: most visits never open it. It is prefetched
   once the page is idle, so the first open is still instant. */
const loadAuthModal = () => import("./components/AuthModal");
const AuthModal = lazyWithReload(loadAuthModal);

const V2_PROFILE_API = "https://us-central1-veyra-200d7.cloudfunctions.net/v2Api/v2GetUserProfile";
const V2_MEDIA_TEAM_API = "https://us-central1-veyra-200d7.cloudfunctions.net/v2Api/v2AdminAction";

const ADMIN_EMAILS = [
  "vladik98@hotmail.de",
  "fortnitetryhard547@gmail.com",
  "asgart@vivaldi.net",
];

const MOD_EMAILS = [
  "asgart@vivaldi.net",
  "fortnitetryhard547@gmail.com",
  "vladik98@hotmail.de",
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  /* Mounted from the first open onwards, so its close animation still runs. */
  const [authModalUsed, setAuthModalUsed] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  /* What the visitor was doing when the modal opened, so the account step
     can say so. Null for the header buttons, which have no such context. */
  const [authContext, setAuthContext] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMediaTeam, setIsMediaTeam] = useState(false);
  const [isMod, setIsMod] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /* Header polish and the Products glow both key off raw scroll offset.
     scrollY is a plain pixel value, so unlike a scroll *progress* value it
     needs no measurement of the scrollable range to be correct. */
  const { scrollY } = useScroll();
  const headerBg = useTransform(scrollY, [0, 120], [
    "rgba(7, 21, 24, 0.55)",
    "rgba(7, 21, 24, 0.92)",
  ]);
  const headerLine = useTransform(scrollY, [0, 120], [
    "rgba(45, 212, 191, 0.10)",
    "rgba(45, 212, 191, 0.26)",
  ]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    return scrollY.on("change", (v) => setShowScrollTop(v > 300));
  }, [scrollY]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    captureReferralCode();
  }, [location]);

  /* Anonymous click positions, recorded only while an admin has the switch on
     in Settings. Mounted here rather than in main.tsx deliberately: the admin
     routes are siblings of this component, not children, so staff clicking
     around the panel can never end up in the visitors' heatmap. */
  useEffect(() => startHeatmap(), []);

  /* Fetch the sign-in popup's code once the page has loaded and gone idle. */
  useEffect(() => {
    const prefetch = () => {
      loadAuthModal().catch(() => {});
    };
    const whenIdle = () => {
      if ("requestIdleCallback" in window) window.requestIdleCallback(prefetch, { timeout: 6000 });
      else setTimeout(prefetch, 3000);
    };
    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });
    return () => window.removeEventListener("load", whenIdle);
  }, []);

  useEffect(() => {
    const splash = document.getElementById("splash-screen");
    if (!splash) return;

    /* The intro belongs to the landing page. Anywhere else it is just a delay
       in front of content the visitor asked for by name -- and worse on a deep
       link, where they are already waiting on a route chunk. */
    if (window.location.pathname !== "/") {
      splash.remove();
      return;
    }

    /* The splash covers the wait for the app, and nothing more. It used to
       hold for a further 500ms after React had mounted, then fade for 600ms,
       and the page itself was not rendered until the hold ended -- so every
       visit to the homepage waited over a second for content that was ready,
       and the footer painted alone under the header before the content
       arrived and shoved it away (a layout shift of 0.75, where under 0.1 is
       good). The page now renders at once underneath, and the splash fades
       on the next frame. On a slow connection it still shows for as long as
       the JavaScript takes to arrive, which is the part that is useful. */
    const timers: number[] = [];
    const frame = requestAnimationFrame(() => {
      splash.style.transition = "opacity .3s ease, visibility .3s ease";
      splash.style.opacity = "0";
      splash.style.visibility = "hidden";
      timers.push(window.setTimeout(() => splash.remove(), 350));
    });

    return () => {
      cancelAnimationFrame(frame);
      timers.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    const gameQuery = new URLSearchParams(location.search).get("game");
    const isInternalGameRoute =
      gameQuery?.toLowerCase() === "deadlock-internal" ||
      gameQuery?.toLowerCase() === "internal";
    /* /product/deadlock-internal too: the path form of the same old id. */
    const pathId = location.pathname.startsWith("/product/")
      ? location.pathname.slice("/product/".length).toLowerCase()
      : null;
    const isProtectedRoute =
      location.pathname === "/product" || location.pathname === "/pricing";
    if (pathId === "deadlock-internal" || pathId === "internal") {
      navigate(productPath("deadlock-external"), { replace: true });
      return;
    }

    if (isProtectedRoute && isInternalGameRoute) {
      /* Deadlock internal is bundled with external now and granted by the same
         purchase, so the old off-site redirect had nothing to send people to.
         The URL stays live because Google has it indexed -- it now lands on the
         bundled product instead of leaving the site. */
      navigate(productPath("deadlock-external"), { replace: true });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        const token = await currentUser.getIdToken();
        try {
          const response = await fetch(V2_PROFILE_API, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          const result = await response.json();

          if (result.success && result.subscriptions) {
            const hasActiveProduct = result.subscriptions.some(
              (sub: { status: string; isPaused: boolean }) =>
                sub.status === "active" && !sub.isPaused,
            );
            setIsSubscriber(hasActiveProduct);
          } else {
            setIsSubscriber(false);
          }

          if (result.success && result.profile) {
            const role = result.profile.role;
            setIsAdmin(role === "admin");
            setIsMod(role === "admin" || role === "moderator");
          } else {
            const email = currentUser.email?.toLowerCase();
            setIsAdmin(!!(email && ADMIN_EMAILS.includes(email)));
            setIsMod(!!(email && MOD_EMAILS.includes(email)));
          }
        } catch (error) {
          console.error("Error checking subscription:", error);
          setIsSubscriber(false);
          const email = currentUser.email?.toLowerCase();
          setIsAdmin(!!(email && ADMIN_EMAILS.includes(email)));
          setIsMod(!!(email && MOD_EMAILS.includes(email)));
        }

        // Check media team membership dynamically from Firestore
        const email = currentUser.email?.toLowerCase();
        if (email) {
          try {
            const mtRes = await fetch(V2_MEDIA_TEAM_API, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ action: "getMediaTeamEmails" }),
            });
            const mtData = await mtRes.json();
            const emails: string[] = mtData.emails ?? [];
            setIsMediaTeam(emails.map((e) => e.toLowerCase()).includes(email));
          } catch {
            setIsMediaTeam(false);
          }
        } else {
          setIsMediaTeam(false);
        }
      } else {
        setIsSubscriber(false);
        setIsAdmin(false);
        setIsMediaTeam(false);
        setIsMod(false);
      }

      if (currentUser && !currentUser.emailVerified) {

        if (
          location.pathname !== "/verify-email" &&
          location.pathname !== "/email-verified" &&
          !location.pathname.startsWith("/invoice") &&
          location.pathname !== "/downloads"
        ) {
          navigate("/verify-email");
        }
      }

      else if (
        currentUser &&
        currentUser.emailVerified &&
        location.pathname === "/verify-email"
      ) {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate, location.pathname]);

  const openAuthModal = (
    mode: "login" | "signup",
    context: string | null = null
  ) => {
    setAuthMode(mode);
    setAuthContext(context);
    setAuthModalUsed(true);
    setAuthModalOpen(true);
    setMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setProfileMenuOpen(false);
      setMenuOpen(false);

      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const maskEmail = (email: string) => {
    const [username, domain] = email.split("@");
    const visibleChars = Math.ceil(username.length * 0.2);
    const maskedLength = username.length - visibleChars;
    return `${username.slice(0, visibleChars)}${"*".repeat(
      maskedLength,
    )}@${domain}`;
  };

  return (
    <div
      className="min-h-screen relative"
      style={{ background: "var(--color-ground)" }}
    >
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-primary focus:text-black focus:px-4 focus:py-2 focus:rounded"
        href="#site-main"
      >
        Skip to content
      </a>

      <motion.header
        className="fixed top-0 left-0 right-0 z-40 border-b"
        style={{
          /* Was flat near-black with a white hairline, which sat apart from a
             page built on the teal ground. Same ground colour, teal hairline,
             and it firms up as you scroll instead of being a constant slab. */
          background: headerBg,
          borderBottomColor: headerLine,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <nav className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between relative">

            <Link
              to="/"
              className="flex items-center gap-2.5 group z-10"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  window.location.reload();
                }
              }}
            >
              <img
                src={logo}
                alt="Veyra - Premium game cheats and hacks"
                className="h-6 transition-transform group-hover:scale-105"
                width={24}
                height={24}
              />
              <span className="text-base font-bold tracking-wider text-white">
                VEYRA
              </span>
            </Link>

            {/* Staff links go icon-only below xl: this group is absolutely centred,
                and with all three it ran into the account button at tablet and
                small-laptop widths. The name stays as a tooltip and label. */}
            <div className="hidden md:flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2">
              <Link
                to="/"
                className={`px-3.5 py-1.5 rounded-full text-sm transition-all ${
                  location.pathname === "/"
                    ? "text-white bg-white/[0.07]"
                    : "text-white/55 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                Home
              </Link>
              {/* No ornament on this link. It carried a blurred halo, then an ESP
                  target bracket, and both read as decoration bolted onto small
                  chrome — four 6px ticks around a 102px box are stray marks, not
                  a reticle. The hero's weapon already aims at this tab, which is
                  the gesture doing the work; the link only needs to sit a little
                  brighter than its neighbours. */}
              <Link
                to="/products"
                className={`px-3.5 py-1.5 rounded-full text-sm transition-all ${
                  location.pathname === "/products"
                    ? "text-white bg-white/[0.07]"
                    : "text-white/75 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                Products
              </Link>
              {isSubscriber && (
                <Link
                  to="/downloads"
                  className="px-3 py-1.5 text-white/60 hover:text-white transition-all font-normal text-sm"
                >
                  Downloads
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  title="Admin"
                  aria-label="Admin"
                  className="px-3 py-1.5 text-red-400 hover:text-red-300 transition-all font-normal text-sm flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="hidden xl:inline">Admin</span>
                </Link>
              )}
              {isMediaTeam && (
                <Link
                  to="/media-team"
                  title="Media Team"
                  aria-label="Media Team"
                  className="px-3 py-1.5 text-purple-400 hover:text-purple-300 transition-all font-normal text-sm flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="hidden xl:inline">Media Team</span>
                </Link>
              )}
              {isMod && (
                <Link
                  to="/moderator"
                  title="Moderator"
                  aria-label="Moderator"
                  className="px-3 py-1.5 text-blue-400 hover:text-blue-300 transition-all font-normal text-sm flex items-center gap-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="hidden xl:inline">Moderator</span>
                </Link>
              )}
            </div>

            <div className="hidden md:flex items-center gap-2 z-10">
              {user ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    aria-label="Account menu"
                    className="flex items-center gap-2 px-2 lg:px-3 py-1.5 bg-white/5 hover:bg-white/8 rounded-md transition-all"
                  >
                    {/* An initial below lg: the masked address is ~250px wide
                        and, beside a staff account's extra links, pushed into
                        them. The full address is in the menu it opens. */}
                    <span
                      aria-hidden
                      className="grid h-6 w-6 place-items-center rounded-full bg-primary/20 text-[0.7rem] font-semibold uppercase text-primary-light lg:hidden"
                    >
                      {(user.email ?? "U").charAt(0)}
                    </span>
                    <span className="hidden text-white/70 text-sm font-normal lg:inline">
                      {user.email ? maskEmail(user.email) : "User"}
                    </span>
                    <svg
                      className={`w-3.5 h-3.5 text-white/70 transition-transform ${profileMenuOpen ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {profileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-56 bg-black/95 backdrop-blur-xl border border-white/10 rounded-md shadow-xl overflow-hidden z-50"
                      >
                        <div className="p-3 border-b border-white/10">
                          <p className="text-white text-sm font-semibold truncate">
                            {user.email}
                          </p>
                          <p className="text-white/40 text-xs mt-0.5">
                            {isSubscriber ? "Active Subscriber" : "Signed in"}
                          </p>
                        </div>
                        <Link
                          to="/profile"
                          onClick={() => setProfileMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm font-medium border-b border-white/10"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          Profile
                        </Link>
                        {isSubscriber && (
                          <Link
                            to="/downloads"
                            onClick={() => setProfileMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm font-medium border-b border-white/10"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                              />
                            </svg>
                            Downloads
                          </Link>
                        )}
                        <Link
                          to="/redeem"
                          onClick={() => setProfileMenuOpen(false)}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm font-medium border-b border-white/10"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                            />
                          </svg>
                          Redeem Code
                        </Link>
                        <button
                          type="button"
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => openAuthModal("login")}
                    className="px-4 py-1.5 text-white/60 hover:text-white transition-all font-normal text-sm"
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    onClick={() => openAuthModal("signup")}
                    className="px-5 py-1.5 bg-primary hover:bg-primary/90 rounded-md text-[#06211f] font-medium text-sm transition-colors"
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden flex flex-col gap-1.5 w-8 h-8 justify-center items-center"
              aria-label="Toggle menu"
            >
              <span
                className={`block h-0.5 w-6 bg-white transition-all ${menuOpen ? "rotate-45 translate-y-2" : ""
                  }`}
              />
              <span
                className={`block h-0.5 w-6 bg-white transition-all ${menuOpen ? "opacity-0" : ""
                  }`}
              />
              <span
                className={`block h-0.5 w-6 bg-white transition-all ${menuOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
              />
            </button>
          </div>

          {menuOpen && (
            <div className="md:hidden mt-4 pt-4 border-t border-white/5 flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
              >
                Home
              </Link>
              <Link
                to="/products"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
              >
                Products
              </Link>
              {isSubscriber && (
                <Link
                  to="/downloads"
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-2.5 text-primary hover:text-primary-light hover:bg-primary/10 rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                    />
                  </svg>
                  Downloads
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Admin
                </Link>
              )}
              {isMediaTeam && (
                <Link
                  to="/media-team"
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-2.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Media Team
                </Link>
              )}
              {isMod && (
                <Link
                  to="/moderator"
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-2.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Moderator
                </Link>
              )}
              <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-white/10">
                {user ? (
                  <>
                    <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-white text-sm font-semibold truncate">
                        {user.email ? maskEmail(user.email) : "User"}
                      </p>
                      <p className="text-white/50 text-xs mt-0.5">Signed in</p>
                    </div>
                    <Link
                      to="/redeem"
                      onClick={() => setMenuOpen(false)}
                      className="px-4 py-2.5 text-center flex items-center justify-center gap-2 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                        />
                      </svg>
                      Redeem Code
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => setMenuOpen(false)}
                      className="px-4 py-2.5 text-center flex items-center justify-center gap-2 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="px-4 py-2.5 text-center flex items-center justify-center gap-2 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => openAuthModal("login")}
                      className="px-4 py-2.5 text-center text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all font-medium"
                    >
                      Log in
                    </button>
                    <button
                      type="button"
                      onClick={() => openAuthModal("signup")}
                      className="px-4 py-2.5 text-center bg-primary hover:bg-primary-dark rounded-lg text-[#06211f] font-semibold transition-colors"
                    >
                      Sign up
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </motion.header>

      {/* pt matches the header height exactly. At pt-20 against a 64px
          header, 16px of the near-black body background showed through as a
          stray dark band under the chrome. */}
      <main id="site-main" className="relative z-10 pt-16">
        {/* Rendered at once, not after the splash: see the splash effect. */}
        <Outlet
          context={{
            openAuthModal,
            user,
          }}
        />
      </main>
      <LanguageSuggest />

      {authModalUsed && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            initialMode={authMode}
            context={authContext}
          />
        </Suspense>
      )}

      <footer className="relative z-10 border-t border-white/5 pt-14 pb-8 overflow-hidden">
        <div className="absolute inset-x-0 bottom-0 h-48 pointer-events-none bg-[radial-gradient(60%_100%_at_50%_100%,rgba(20,184,166,0.08),transparent)]" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <span className="text-xl font-extrabold tracking-wider text-white" style={{ fontFamily: '"Poppins", sans-serif' }}>
                VEYRA
              </span>
              <p className="text-white/50 text-sm leading-relaxed mt-3 max-w-[16rem]">
                Premium enhancement software. Undetected since day one.
              </p>
            </div>

            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em] mb-4">Site</p>
              <div className="flex flex-col gap-2.5 text-sm">
                <Link to="/" className="text-white/65 hover:text-primary-light transition-colors">Home</Link>
                <Link to="/products" className="text-white/65 hover:text-primary-light transition-colors">Products</Link>
                <Link to="/features" className="text-white/65 hover:text-primary-light transition-colors">Features</Link>
              </div>
            </div>

            {/* Static HTML outside the SPA, so plain anchors -- a Link would make
                the router try to match a route that does not exist. Linking them
                site-wide also stops them being orphans: they were reachable only
                from the sitemap, which is enough for a crawler to find them and not
                enough for them to inherit any authority from the rest of the site. */}
            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em] mb-4">Guides</p>
              <div className="flex flex-col gap-2.5 text-sm">
                {/* In-app links now: the guides are routes, no longer static
                    files a full page load had to fetch. */}
                {GUIDE_LINKS.map((g) => (
                  <Link key={g.path} to={g.path} className="text-white/65 hover:text-primary-light transition-colors">
                    {g.label}
                  </Link>
                ))}
                <Link to="/status" className="text-white/65 hover:text-primary-light transition-colors">Cheat status</Link>
                <Link to="/creators" className="text-white/65 hover:text-primary-light transition-colors">Creator program</Link>
                <Link to="/lua-docs/sources" className="text-white/65 hover:text-primary-light transition-colors">Lua scripts</Link>
                <Link to="/lua-docs" className="text-white/65 hover:text-primary-light transition-colors">Lua docs</Link>
              </div>
              {/* The translated Deadlock pages. hreflang alone tells Google
                  they exist; a plain link from every page is what passes
                  them any of the site's weight. */}
              <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em] mb-3 mt-7">Languages</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm">
                {LANGS.map((l) => (
                  <Link key={l} to={`/${l}`} lang={LANG_INFO[l].hreflang} className="text-white/65 hover:text-primary-light transition-colors">
                    {LANG_INFO[l].native}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em] mb-4">Account</p>
              <div className="flex flex-col gap-2.5 text-sm">
                <Link to="/login" className="text-white/65 hover:text-primary-light transition-colors">Log in</Link>
                <Link to="/signup" className="text-white/65 hover:text-primary-light transition-colors">Sign up</Link>
                <Link to="/downloads" className="text-white/65 hover:text-primary-light transition-colors">Downloads</Link>
              </div>
            </div>

            <div>
              <p className="text-white/60 text-xs font-semibold uppercase tracking-[0.2em] mb-4">Community</p>
              <div className="flex flex-col gap-2.5 text-sm">
                <a
                  href="https://discord.gg/d64RvvK9Ta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/65 hover:text-primary-light transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                  Discord
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
            <div className="flex items-center gap-6 text-xs">
              <Link
                to="/terms"
                className="text-white/50 hover:text-white transition-colors"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy"
                className="text-white/50 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
            {/* The end year comes from the clock rather than the source, so it
                stops being a thing to remember every January -- and a footer
                reading last year is a small, constant signal that nobody is
                home. */}
            <p className="text-white/50 text-xs">
              Copyright © Veyra 2024-{new Date().getFullYear()}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            aria-label="Scroll to top"
            className="fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-[rgba(7,21,24,0.85)] text-primary-light shadow-lg backdrop-blur-md transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
